import { useState, useEffect } from 'react'
import { FiX, FiClock, FiCalendar, FiAlertCircle, FiCoffee } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { API_URL } from '../../config/api'
import { Btn, Pill } from '../../ui'
import { formatearFecha, formatearHora } from '../../utils/fechas'

/*
 * SolicitudCorreccionModal (V2) — DS v2.
 * Misma lógica que el legacy: corrección de fichaje existente, fichaje
 * olvidado individual, o múltiples fechas.
 *
 * En el caso de fichaje olvidado, hace prefetch del calendario laboral del
 * empleado para autocompletar entrada y salida con `hora_entrada_min` y
 * `hora_salida_min` y, si el calendario marca pausa obligatoria, ofrece un
 * bloque "Pausa" pre-rellenado con el tipo por defecto y la duración del
 * calendario en la mitad de la jornada. Todo editable.
 */

// Devuelve la hora de inicio de la pausa centrada en la jornada propuesta,
// dado un intervalo de entrada/salida y la duración deseada en minutos.
// La duración se mantiene exactamente como viene (no se recorta) para que
// el campo "Duración" del modal sea consistente con lo que muestra el form.
const calcularInicioPausa = (entrada, salida, duracionMin) => {
  if (!entrada || !salida) return '14:00'
  const [eh, em] = entrada.split(':').map(Number)
  const [sh, sm] = salida.split(':').map(Number)
  const ini = eh * 60 + em
  const fin = sh * 60 + sm
  if (fin <= ini) return '14:00'
  const dur = Math.max(5, duracionMin || 30)
  const mid = Math.round((ini + fin) / 2)
  // Centramos la pausa, garantizando que cabe dentro de la jornada con margen.
  const inicioMin = Math.max(ini + 30, Math.min(mid - Math.floor(dur / 2), fin - dur - 30))
  const fmt = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
  return fmt(Math.max(ini + 5, inicioMin))
}

// Suma minutos a una hora "HH:MM" y devuelve "HH:MM".
const sumarMinutos = (hora, minutos) => {
  if (!hora) return ''
  const [h, m] = hora.split(':').map(Number)
  const total = h * 60 + m + (minutos || 0)
  const hh = Math.floor((total % (24 * 60)) / 60)
  const mm = total % 60
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}

// Diferencia en minutos entre dos horas "HH:MM" (asume mismo día). Si la
// segunda es anterior, devuelve 0.
const diffMinutos = (desde, hasta) => {
  if (!desde || !hasta) return 0
  const [dh, dm] = desde.split(':').map(Number)
  const [hh, hm] = hasta.split(':').map(Number)
  const d = dh * 60 + dm
  const h = hh * 60 + hm
  return Math.max(0, h - d)
}

const SolicitudCorreccionModalV2 = ({ fichaje, fechaSugerida, fechasMultiples, onClose, onSuccess }) => {
  const [motivo, setMotivo] = useState('')
  const [nuevaHoraEntrada, setNuevaHoraEntrada] = useState(fichaje?.hora_entrada?.substring(0, 5) || '')
  const [nuevaHoraSalida, setNuevaHoraSalida] = useState(fichaje?.hora_salida?.substring(0, 5) || '')
  const [fechaOlvidada, setFechaOlvidada] = useState(fechaSugerida || '')
  const [loading, setLoading] = useState(false)
  const [calendario, setCalendario] = useState(null)
  const [tiposPausa, setTiposPausa] = useState([])
  const [incluirPausa, setIncluirPausa] = useState(false)
  const [tipoPausaId, setTipoPausaId] = useState(null)
  const [horaInicioPausa, setHoraInicioPausa] = useState('')
  const [duracionPausaMin, setDuracionPausaMin] = useState(30)
  // De dónde vinieron los valores autocompletados, para mostrarlo al usuario.
  // 'fichaje' = del último fichaje del mismo día de la semana (preferido).
  // 'calendario' = fallback al calendario laboral cuando no hay histórico.
  const [fuenteAuto, setFuenteAuto] = useState('') // '' | 'fichaje' | 'calendario'
  const [fechaReferencia, setFechaReferencia] = useState('')

  const esFichajeOlvidado = !fichaje
  const esMultiple = fechasMultiples && fechasMultiples.length > 1

  // Carga única: catálogo de tipos_pausa + calendario laboral del empleado.
  // Sirve de fallback cuando el endpoint /ultimo-mismo-dia-semana no encuentra
  // un fichaje previo del mismo día de la semana.
  useEffect(() => {
    if (!esFichajeOlvidado) return
    let cancelado = false
    const cargar = async () => {
      try {
        const token = localStorage.getItem('token')
        const r = await fetch(`${API_URL}/calendarios-laborales/mio`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!r.ok) return
        const data = await r.json()
        if (cancelado) return
        setCalendario(data.calendario || null)
        setTiposPausa(data.tipos_pausa || [])
      } catch {
        // Silencio: si falla, el modal sigue funcionando con los campos vacíos.
      }
    }
    cargar()
    return () => { cancelado = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Cada vez que cambia la fecha del fichaje olvidado, intentamos autocompletar
  // con los datos del último fichaje del mismo día de la semana del propio
  // empleado. Si no hay histórico, fallback al calendario laboral.
  useEffect(() => {
    if (!esFichajeOlvidado || !fechaOlvidada) return
    let cancelado = false
    const cargar = async () => {
      const token = localStorage.getItem('token')

      // 1) Preferido: último fichaje del mismo día de la semana.
      try {
        const r = await fetch(
          `${API_URL}/fichajes/ultimo-mismo-dia-semana?fecha=${encodeURIComponent(fechaOlvidada)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        if (r.ok) {
          const data = await r.json()
          if (cancelado) return
          if (data.fichaje) {
            setNuevaHoraEntrada(data.fichaje.hora_entrada || '')
            setNuevaHoraSalida(data.fichaje.hora_salida || '')
            if (data.pausa) {
              // Preferimos duracion_minutos del backend; si por alguna razón
              // no llega, la calculamos del par inicio/fin de la pausa pasada.
              const dur = data.pausa.duracion_minutos
                || diffMinutos(data.pausa.hora_inicio, data.pausa.hora_fin)
                || 30
              setIncluirPausa(true)
              setTipoPausaId(data.pausa.tipo_pausa_id)
              setHoraInicioPausa(data.pausa.hora_inicio || '')
              setDuracionPausaMin(dur)
            } else {
              setIncluirPausa(false)
            }
            setFuenteAuto('fichaje')
            setFechaReferencia(data.fecha_referencia || '')
            return
          }
        }
      } catch { /* caemos al fallback */ }

      // 2) Fallback: ventana del calendario laboral.
      if (cancelado || !calendario) return
      const entrada = (calendario.hora_entrada_min || '').slice(0, 5)
      const salida = (calendario.hora_salida_min || '').slice(0, 5)
      if (entrada) setNuevaHoraEntrada(entrada)
      if (salida) setNuevaHoraSalida(salida)
      if (calendario.pausa_obligatoria === 1 && tiposPausa.length > 0) {
        const dur = calendario.duracion_pausa_min || 30
        const inicio = calcularInicioPausa(entrada, salida, dur)
        setIncluirPausa(true)
        setTipoPausaId(tiposPausa[0].id)
        setHoraInicioPausa(inicio)
        setDuracionPausaMin(dur)
      } else {
        setIncluirPausa(false)
      }
      setFuenteAuto('calendario')
      setFechaReferencia('')
    }
    cargar()
    return () => { cancelado = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaOlvidada, calendario, tiposPausa])

  const handleSubmit = async (e) => {
    e.preventDefault()
    // Motivo es opcional para fichajes olvidados (el contexto suele bastar).
    // Para correcciones de fichajes existentes lo seguimos exigiendo: ahí el
    // admin necesita saber por qué se cambió un dato concreto.
    if (!esFichajeOlvidado && !motivo.trim()) {
      toast.error('Debes indicar el motivo de la corrección'); return
    }
    if (esFichajeOlvidado) {
      if (!fechaOlvidada) { toast.error('Debes seleccionar la fecha del fichaje olvidado'); return }
      if (!nuevaHoraEntrada || !nuevaHoraSalida) {
        toast.error('Para un fichaje olvidado debes indicar tanto la hora de entrada como la de salida'); return
      }
      if (incluirPausa) {
        if (!tipoPausaId || !horaInicioPausa || !duracionPausaMin) {
          toast.error('Si incluyes una pausa, indica tipo, hora de inicio y duración'); return
        }
        if (duracionPausaMin < 1) {
          toast.error('La duración de la pausa debe ser de al menos 1 minuto'); return
        }
        const horaFinCalc = sumarMinutos(horaInicioPausa, duracionPausaMin)
        if (horaInicioPausa <= nuevaHoraEntrada || horaFinCalc >= nuevaHoraSalida) {
          toast.error('La pausa debe estar dentro de la jornada (entre la entrada y la salida)'); return
        }
      }
    } else {
      if (!nuevaHoraEntrada && !nuevaHoraSalida) {
        toast.error('Debes indicar al menos una hora a corregir'); return
      }
    }

    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      const fechasAEnviar = esMultiple ? fechasMultiples : [esFichajeOlvidado ? fechaOlvidada : fichaje.fecha]
      let enviadas = 0, errores = 0

      const pausaPayload = (esFichajeOlvidado && incluirPausa) ? {
        tipo_pausa_id: parseInt(tipoPausaId, 10),
        hora_inicio: horaInicioPausa,
        // El backend espera hora_fin; el formulario trabaja con duración en
        // minutos (más natural para el usuario) y la convertimos aquí.
        hora_fin: sumarMinutos(horaInicioPausa, duracionPausaMin),
      } : null

      for (const fecha of fechasAEnviar) {
        const requestBody = esFichajeOlvidado || esMultiple
          ? { fecha, motivo, hora_entrada_corregida: nuevaHoraEntrada, hora_salida_corregida: nuevaHoraSalida, pausa_solicitada: pausaPayload }
          : { fichaje_id: fichaje.id, fecha: fichaje.fecha, motivo, hora_entrada_corregida: nuevaHoraEntrada || null, hora_salida_corregida: nuevaHoraSalida || null }

        try {
          const response = await fetch(`${API_URL}/correcciones`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(requestBody)
          })
          if (response.ok) enviadas++; else errores++
        } catch { errores++ }
      }

      if (errores > 0 && enviadas > 0) {
        toast.success(`${enviadas} solicitud(es) enviada(s), ${errores} con error`)
      } else if (errores > 0) {
        throw new Error('Error al enviar las solicitudes')
      } else {
        toast.success(enviadas > 1 ? `${enviadas} solicitudes de corrección enviadas` : 'Solicitud de corrección enviada correctamente')
      }
      onSuccess()
    } catch (error) {
      console.error('Error:', error)
      toast.error(error.message || 'Error al solicitar corrección')
    } finally {
      setLoading(false)
    }
  }

  const titulo = esMultiple
    ? `Corrección de ${fechasMultiples.length} días`
    : esFichajeOlvidado ? 'Solicitar fichaje olvidado' : 'Solicitar corrección'

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="ui-card w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[var(--line)]">
          <div>
            <div className="eyebrow mb-1">{esFichajeOlvidado ? 'Fichaje olvidado' : 'Corrección'}</div>
            <h3 className="font-display text-[22px] text-[var(--ink)] leading-tight">{titulo}.</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-soft text-[var(--ink-3)] hover:text-[var(--ink)] hover:bg-[var(--bg-2)] transition-colors"
            aria-label="Cerrar"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Info del fichaje existente */}
        {!esFichajeOlvidado && (
          <div className="p-4 bg-[var(--bg-2)] border-b border-[var(--line)]">
            <div className="flex items-center gap-2 text-[12.5px] text-[var(--ink-3)] mb-2">
              <FiCalendar size={13} />
              <span className="capitalize">
                {formatearFecha(fichaje.fecha)}
              </span>
            </div>
            <div className="flex items-center gap-4 text-[13px]">
              <div className="flex items-center gap-1.5">
                <FiClock size={13} className="text-[#0F8C66]" />
                <span className="text-[var(--ink-3)]">Entrada:</span>
                <strong className="font-mono tnum text-[var(--ink)]">{formatearHora(fichaje.hora_entrada)}</strong>
              </div>
              {fichaje.hora_salida && (
                <div className="flex items-center gap-1.5">
                  <FiClock size={13} className="text-[#C13030]" />
                  <span className="text-[var(--ink-3)]">Salida:</span>
                  <strong className="font-mono tnum text-[var(--ink)]">{formatearHora(fichaje.hora_salida)}</strong>
                </div>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Resumen múltiple */}
          {esMultiple && (
            <div className="p-3 rounded-soft bg-[rgba(37,99,235,.04)] border border-[rgba(37,99,235,.15)]">
              <p className="text-[12.5px] font-medium text-[var(--brand-1)] mb-2">
                Se aplicará a {fechasMultiples.length} días:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {fechasMultiples.map(f => (
                  <Pill key={f} tone="brand">
                    {formatearFecha(f)}
                  </Pill>
                ))}
              </div>
            </div>
          )}

          {/* Fecha olvidada */}
          {esFichajeOlvidado && !esMultiple && (
            <div>
              <label className="eyebrow block mb-1.5">Fecha del fichaje olvidado *</label>
              <input
                type="date"
                value={fechaOlvidada}
                onChange={(e) => setFechaOlvidada(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft font-mono text-[14px] focus:outline-none focus:border-[var(--ink-3)]"
                required
              />
            </div>
          )}

          {/* Motivo */}
          <div>
            <label className="eyebrow block mb-1.5">
              {esFichajeOlvidado ? 'Motivo del olvido' : 'Motivo de la corrección *'}
            </label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[13px] resize-none focus:outline-none focus:border-[var(--ink-3)]"
              placeholder={esFichajeOlvidado
                ? 'Opcional. Añade un comentario si quieres explicar al admin por qué no fichaste.'
                : 'Explica el motivo de la corrección…'}
              required={!esFichajeOlvidado}
            />
          </div>

          {/* Hora entrada y salida */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow block mb-1.5">
                {esFichajeOlvidado ? 'Hora de entrada *' : 'Nueva entrada'}
              </label>
              <input
                type="time"
                value={nuevaHoraEntrada}
                onChange={(e) => setNuevaHoraEntrada(e.target.value)}
                className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft font-mono text-[14px] focus:outline-none focus:border-[var(--ink-3)]"
                required={esFichajeOlvidado}
              />
            </div>
            <div>
              <label className="eyebrow block mb-1.5">
                {esFichajeOlvidado ? 'Hora de salida *' : 'Nueva salida'}
              </label>
              <input
                type="time"
                value={nuevaHoraSalida}
                onChange={(e) => setNuevaHoraSalida(e.target.value)}
                className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft font-mono text-[14px] focus:outline-none focus:border-[var(--ink-3)]"
                required={esFichajeOlvidado}
              />
            </div>
          </div>
          {esFichajeOlvidado && fuenteAuto === 'fichaje' && fechaReferencia && (
            <p className="text-[11px] text-[var(--ink-3)] -mt-2">
              Autocompletado según tu fichaje del{' '}
              {formatearFecha(fechaReferencia)}
              . Puedes editarlo.
            </p>
          )}
          {esFichajeOlvidado && fuenteAuto === 'calendario' && calendario && (
            <p className="text-[11px] text-[var(--ink-3)] -mt-2">
              Autocompletado desde tu horario laboral ({calendario.nombre}). Puedes editarlo.
            </p>
          )}

          {/* Bloque pausa (solo en fichaje olvidado y si hay tipos de pausa disponibles) */}
          {esFichajeOlvidado && tiposPausa.length > 0 && (
            <div className="border border-[var(--line)] rounded-soft overflow-hidden">
              <label className="flex items-center justify-between gap-2 p-3 bg-[var(--bg-2)] cursor-pointer">
                <div className="flex items-center gap-2 text-[13px] font-medium text-[var(--ink)]">
                  <FiCoffee size={14} className="text-[#A86A06]" />
                  <span>Incluir una pausa ese día</span>
                  {calendario?.pausa_obligatoria === 1 && (
                    <Pill tone="amber">Obligatoria según horario</Pill>
                  )}
                </div>
                <input
                  type="checkbox"
                  checked={incluirPausa}
                  onChange={(e) => setIncluirPausa(e.target.checked)}
                  className="w-4 h-4 accent-[var(--brand-1)]"
                />
              </label>
              {incluirPausa && (
                <div className="p-3 space-y-3">
                  <div>
                    <label className="eyebrow block mb-1.5">Tipo de pausa *</label>
                    <select
                      value={tipoPausaId || ''}
                      onChange={(e) => setTipoPausaId(parseInt(e.target.value, 10))}
                      className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[13px] focus:outline-none focus:border-[var(--ink-3)]"
                      required
                    >
                      {tiposPausa.map((t) => (
                        <option key={t.id} value={t.id}>{t.nombre}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="eyebrow block mb-1.5">Inicio pausa *</label>
                      <input
                        type="time"
                        value={horaInicioPausa}
                        onChange={(e) => setHoraInicioPausa(e.target.value)}
                        className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft font-mono text-[14px] focus:outline-none focus:border-[var(--ink-3)]"
                        required
                      />
                    </div>
                    <div>
                      <label className="eyebrow block mb-1.5">Duración (min) *</label>
                      <input
                        type="number"
                        min="1"
                        max="600"
                        step="5"
                        value={duracionPausaMin}
                        onChange={(e) => setDuracionPausaMin(parseInt(e.target.value, 10) || 0)}
                        className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft font-mono text-[14px] focus:outline-none focus:border-[var(--ink-3)]"
                        required
                      />
                      {horaInicioPausa && duracionPausaMin > 0 && (
                        <p className="text-[11px] text-[var(--ink-3)] mt-1 font-mono tnum">
                          Termina a las {sumarMinutos(horaInicioPausa, duracionPausaMin)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Info */}
          <div className="flex items-start gap-2 p-3 rounded-soft bg-[rgba(37,99,235,.04)] border border-[rgba(37,99,235,.15)]">
            <FiAlertCircle className="text-[var(--brand-1)] mt-0.5 flex-shrink-0" size={14} />
            <p className="text-[11.5px] text-[var(--brand-1)]">
              {esFichajeOlvidado
                ? 'La solicitud será revisada por un administrador. Debes indicar la fecha y las horas de entrada y salida.'
                : 'La solicitud será revisada por un administrador. Solo debes rellenar las horas que necesites corregir.'}
            </p>
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-1">
            <Btn type="button" variant="ghost" className="flex-1" onClick={onClose} disabled={loading}>
              Cancelar
            </Btn>
            <Btn type="submit" variant="primary" className="flex-1" disabled={loading}>
              {loading ? 'Enviando…' : 'Enviar solicitud'}
            </Btn>
          </div>
        </form>
      </div>
    </div>
  )
}

export default SolicitudCorreccionModalV2
